import { useQuery } from '@tanstack/react-query';
import { useAtom, useSetAtom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithStorage, createJSONStorage, RESET } from 'jotai/utils';
import { useCallback } from 'react';
import { queryMe } from '../me/api';
import {
  getVideoReplicationResultDraftStorageKey,
  isVideoReplicationResultDraft,
  type VideoReplicationResultDraft,
} from './storage';

const sessionStorageJson = createJSONStorage<object>(() => sessionStorage);
const sessionStorageAdapter = {
  getItem: (key: string, initialValue: VideoReplicationResultDraft) => {
    const value = sessionStorageJson.getItem(key, initialValue);
    return isVideoReplicationResultDraft(value) ? value : initialValue;
  },
  setItem: (key: string, value: VideoReplicationResultDraft) =>
    sessionStorageJson.setItem(key, value),
  removeItem: (key: string) => sessionStorageJson.removeItem(key),
};

const videoReplicationResultDraftFamilyAtom = atomFamily((storageKey: string) =>
  atomWithStorage<VideoReplicationResultDraft>(
    storageKey,
    {},
    sessionStorageAdapter,
    { getOnInit: true },
  ),
);

function useVideoReplicationResultDraftAtom(taskId: string) {
  const { data } = useQuery(queryMe);
  return videoReplicationResultDraftFamilyAtom(
    getVideoReplicationResultDraftStorageKey(data.name, taskId),
  );
}

export function useVideoReplicationResultDraft(taskId: string) {
  return useAtom(useVideoReplicationResultDraftAtom(taskId));
}

export function useResetVideoReplicationResultDraft(taskId: string) {
  const setDraft = useSetAtom(useVideoReplicationResultDraftAtom(taskId));

  return useCallback(() => setDraft(RESET), [setDraft]);
}

const videoReplicationCurrentTaskIdFamilyAtom = atomFamily(
  (accountId: string) =>
    atomWithStorage<string | null>(
      `${accountId}_v1_video_replication_current_task_id`,
      null,
      createJSONStorage<string | null>(() => sessionStorage),
      { getOnInit: true },
    ),
);

export function useVideoReplicationCurrentTaskId() {
  const { data } = useQuery(queryMe);
  return useAtom(videoReplicationCurrentTaskIdFamilyAtom(data.name));
}
